import { useEffect, useRef, useState } from "react"

import type { ConversationsTableFilters } from "../types"

const HIGHLIGHT_DURATION_MS = 3000

interface ConversationsSavedState {
  clickedRowId: string
  filters?: ConversationsTableFilters
  scrollOffset: number
  selectedKeys?: string[]
  selectedRunIdsBySimulationId?: Record<string, string>
}

interface ConversationsSessionState {
  clearSavedState: () => void
  getSavedState: () => ConversationsSavedState | null
}

interface UseConversationsTableSessionRestoreParams {
  /** Filters the list is mounting with, from the URL. */
  filters: ConversationsTableFilters
  /** Whether `filters` has been read from the URL yet. */
  isFilterReady: boolean
  onRestoreSelection: (keys: string[]) => void
  sessionState: ConversationsSessionState
}

/**
 * Saved state belongs to the list it was saved from. A list opened with
 * different filters (e.g. a dashboard drilldown in the same tab) is a new
 * visit, so its scroll, highlight and selection start fresh.
 */
function isSameList(
  saved: ConversationsTableFilters | undefined,
  current: ConversationsTableFilters,
): boolean {
  return saved === undefined || listKey(saved) === listKey(current)
}

/** Order-insensitive key: filter values are sets, whatever order the URL lists them in. */
function listKey(filters: ConversationsTableFilters): string {
  const entries: [string, unknown][] = Object.entries(filters)
  return JSON.stringify(
    entries
      .filter(([, value]) => value !== undefined)
      .map(([key, value]): [string, unknown] => [
        key,
        Array.isArray(value)
          ? (value as unknown[]).map((item) => JSON.stringify(item)).sort()
          : value,
      ])
      .sort(([left], [right]) => left.localeCompare(right)),
  )
}

export function useConversationsTableSessionRestore({
  filters,
  isFilterReady,
  onRestoreSelection,
  sessionState,
}: UseConversationsTableSessionRestoreParams) {
  const hasRestoredRef = useRef(false)
  const getSavedStateRef = useRef(sessionState.getSavedState)
  const clearSavedStateRef = useRef(sessionState.clearSavedState)
  const [initialScrollOffset, setInitialScrollOffset] = useState<
    number | undefined
  >(undefined)
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null)
  const [restoredRunIdsBySimulationId, setRestoredRunIdsBySimulationId] =
    useState<Record<string, string>>({})

  const filtersRef = useRef(filters)

  getSavedStateRef.current = sessionState.getSavedState
  clearSavedStateRef.current = sessionState.clearSavedState
  filtersRef.current = filters

  useEffect(() => {
    if (hasRestoredRef.current || !isFilterReady) {
      return
    }

    hasRestoredRef.current = true
    const savedState = getSavedStateRef.current()
    if (!savedState) {
      return
    }
    if (!isSameList(savedState.filters, filtersRef.current)) {
      clearSavedStateRef.current()
      return
    }

    setInitialScrollOffset(savedState.scrollOffset)
    setHighlightedRowId(savedState.clickedRowId)

    if (savedState.selectedKeys && savedState.selectedKeys.length > 0) {
      onRestoreSelection(savedState.selectedKeys)
      setRestoredRunIdsBySimulationId(
        savedState.selectedRunIdsBySimulationId ?? {},
      )
    }
  }, [isFilterReady, onRestoreSelection])

  useEffect(() => {
    if (!highlightedRowId) {
      return
    }

    clearSavedStateRef.current()
    const timeoutId = window.setTimeout(() => {
      setHighlightedRowId(null)
    }, HIGHLIGHT_DURATION_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [highlightedRowId])

  return {
    highlightedRowId,
    initialScrollOffset,
    restoredRunIdsBySimulationId,
  }
}
